import Highcharts from 'highcharts';
import _HighchartsReact from 'highcharts-react-official';
import { Text, View, fromTheme, hooks, styled } from 'lib_ui-primitives';
import { createElement as rc, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
const { useMemoizedObject } = hooks;
const HighchartsReact = _HighchartsReact.default || _HighchartsReact;
HighchartsReact.displayName = 'HighchartsReact';

const Stack = styled(View).attrs({ name: 'HorizontalChartStacked' })`
    flex-direction: column;
    height: 100%;
    width: 100%;
    align-items: stretch;
    max-width: ${fromTheme('charts', 'maxWidth')};
`;

const ChartContainer = styled(View)`
    flex-grow: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    padding: ${fromTheme('charts', 'bar', 'padding')};
`;

const LegendContainer = styled(View)`
    flex-direction: column;
    align-items: flex-start;
    padding-top: ${fromTheme('charts', 'legend', 'itemSpacing')};
    margin-top: auto;
    padding: ${fromTheme('charts', 'bar', 'horizontal', 'legendPadding')};
`;

const LegendItem = styled(View)`
    flex-direction: row;
    align-items: center;
    margin-top: ${fromTheme('charts', 'legend', 'itemSpacing')};
    cursor: pointer;
    user-select: none;
    &:hover {
        opacity: 0.7;
    }
`;

const LegendDot = styled(View)`
    width: ${fromTheme('charts', 'legend', 'dotSize')};
    height: ${fromTheme('charts', 'legend', 'dotSize')};
    border-radius: ${fromTheme('charts', 'legend', 'dotRadius')};
    margin-right: ${fromTheme('charts', 'legend', 'itemGap')};
`;

const LegendText = styled(Text)`
    font-family: ${fromTheme('charts', 'legend', 'labelFont', 'fontFamily')};
    font-size: ${fromTheme('charts', 'legend', 'labelFont', 'fontSize')};
    font-weight: ${fromTheme('charts', 'legend', 'labelFont', 'fontWeight')};
    color: ${({ disabled, theme }) => (disabled ? theme.disabledFontColor : theme.defaultFontColor)};
`;

const EMPTY_ARRAY = [];

function HorizontalChartStacked(props) {
    const { data = EMPTY_ARRAY } = props || {};

    const theme = useTheme();
    const charts = theme.charts;
    const chartColors = theme.chartColors;
    const memoizedData = useMemoizedObject(data);
    const chartRef = useRef(null);
    const [hiddenIndices, setHiddenIndices] = useState(new Set());

    const options = useMemo(() => {
        return {
            chart: {
                type: 'bar',
                backgroundColor: 'transparent',
                styledMode: true
            },
            title: { text: '' },
            tooltip: {
                pointFormat: '{series.name}: <b>{point.y}</b><br/>% of Total: <b>{point.percentage:.1f}%</b>'
            },
            colors: chartColors,
            plotOptions: {
                series: {
                    stacking: 'percent',
                    pointWidth: charts.bar.height,
                    borderWidth: 0,
                    borderRadius: charts.bar.borderRadius,
                    states: {
                        hover: {
                            brightness: 0.2
                        }
                    }
                }
            },
            credits: { enabled: false },
            legend: {
                enabled: false
            },
            series: memoizedData.map((x, i) => ({
                name: x.name || x._id?.title || 'None',
                data: [x.y ?? x.count],
                color: chartColors[i % chartColors.length],
                visible: !hiddenIndices.has(i),
                custom: {
                    total: x.y ?? x.count
                }
            })),
            yAxis: {
                visible: false
            },
            xAxis: {
                categories: [''],
                visible: false
            }
        };
    }, [memoizedData, charts, chartColors, hiddenIndices]);

    const handleLegendClick = index => {
        setHiddenIndices(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    const handleMouseEnter = index => {
        const chart = chartRef.current;
        if (chart && chart.series[index] && !hiddenIndices.has(index)) {
            const series = chart.series[index];
            series.setState('hover');
            if (series.points && series.points[0]) {
                series.points[0].setState('hover');
                chart.tooltip.refresh(series.points[0]);
            }
        }
    };

    const handleMouseLeave = index => {
        const chart = chartRef.current;
        if (chart && chart.series[index]) {
            const series = chart.series[index];
            series.setState('');
            if (series.points && series.points[0]) {
                series.points[0].setState('');
            }
            chart.tooltip.hide();
        }
    };

    const legendItems = memoizedData.map((x, i) => {
        const isDisabled = hiddenIndices.has(i);
        return rc(
            LegendItem,
            {
                key: i,
                onClick: () => handleLegendClick(i),
                onMouseEnter: () => handleMouseEnter(i),
                onMouseLeave: () => handleMouseLeave(i)
            },
            rc(LegendDot, {
                style: {
                    backgroundColor: isDisabled ? theme.disabledFontColor : chartColors[i % chartColors.length],
                    opacity: isDisabled ? 0.5 : 1
                }
            }),
            rc(LegendText, { theme, disabled: isDisabled }, `${x.name || x._id?.title || 'None'} - ${x.y ?? x.count}`)
        );
    });

    return rc(
        Stack,
        null,
        rc(
            ChartContainer,
            null,
            rc(HighchartsReact, {
                highcharts: Highcharts,
                options,
                height: '100%',
                width: '100%',
                callback: chart => {
                    chartRef.current = chart;
                }
            })
        ),
        rc(LegendContainer, null, legendItems)
    );
}

export default HorizontalChartStacked;
